/**
 * Bank-style loan application document.
 *
 * Renders a saved finance / hand-loan record as a formal "application form"
 * (the kind a borrower signs at a bank branch) and prints it as a PDF via the
 * shared branded print pipeline in exportReports.
 */
import { exportHtmlToPdf } from './exportReports';
import { mediaSrc } from './mediaUrl';
import { generateHdQrDataUrl, HD_QR_IMG_STYLE } from './hdQr';
import { parseHandLoanRepaymentType, HAND_LOAN_RATE_LABEL } from './handLoanMath';

export type LoanKind = 'finance' | 'handloan';

type Dict = Record<string, unknown>;

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function val(d: Dict, ...keys: string[]): string {
  for (const k of keys) {
    const v = d[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') return String(v);
  }
  return '—';
}

function field(label: string, value: string): string {
  return `
    <div style="border:1px solid #cbd5e1;border-radius:6px;padding:8px 11px 11px;background:#ffffff;break-inside:avoid;line-height:1.35;">
      <div style="font-size:9.5px;text-transform:uppercase;letter-spacing:.05em;color:#64748b;font-weight:700;line-height:1.35;">${esc(label)}</div>
      <div style="font-size:12px;color:#0f172a;font-weight:600;margin-top:4px;word-break:break-word;line-height:1.4;">${esc(value)}</div>
    </div>`;
}

/**
 * A titled group of fields. `break-inside: avoid` keeps the section heading and
 * its grid together so a section title never lands at the bottom of one page
 * while its data flows onto the next.
 */
function section(title: string, fieldsHtml: string, cols = 3): string {
  return `
    <div style="margin-top:12px;padding-bottom:6px;break-inside:avoid;page-break-inside:avoid;-webkit-column-break-inside:avoid;">
      <div style="display:flex;align-items:center;gap:8px;border-left:4px solid #0f766e;padding:5px 10px;background:#f0fdfa;border-radius:0 6px 6px 0;margin-bottom:8px;">
        <span style="font-size:12px;font-weight:800;color:#0f766e;text-transform:uppercase;letter-spacing:.07em;line-height:1.35;">${esc(title)}</span>
      </div>
      <div style="display:grid;grid-template-columns:repeat(${cols},minmax(0,1fr));gap:8px;">${fieldsHtml}</div>
    </div>`;
}

export function buildLoanApplicationHtml(
  data: Dict,
  kind: LoanKind,
  opts?: { qrDataUrl?: string }
): string {
  const assetTitle = kind === 'finance' ? 'Vehicle / Asset Details' : 'Loan Details';
  const docTitle = kind === 'finance' ? 'VEHICLE FINANCE APPLICATION' : 'HAND LOAN APPLICATION';
  const refNo = val(data, 'haNumber', 'regNo', 'id');

  const applicant = section(
    'Applicant Details',
    [
      field('Full Name', val(data, 'customerName', 'name')),
      field('Mobile Number', val(data, 'mobileNumber', 'phone', 'contact')),
      field('Alternate Mobile', val(data, 'alternateMobile')),
      field('Aadhaar No.', val(data, 'customerAadhar', 'aadharNumber')),
      field('PAN No.', val(data, 'panNumber')),
      field('Date of Birth', val(data, 'dob')),
      field('Village / Town', val(data, 'villageTown')),
      field('Line / Route', val(data, 'line')),
      field('Address Line 1', val(data, 'customerAddress1', 'address1', 'address')),
      field('Address Line 2', val(data, 'customerAddress2', 'address2')),
    ].join(''),
  );

  const guarantor = section(
    'Guarantor / Reference',
    [
      field('Guarantor Name', val(data, 'guarantorName', 'suretyName')),
      field('Guarantor Mobile', val(data, 'guarantorMobile', 'suretyNumber')),
      field('Guarantor Address', val(data, 'guarantorAddress', 'suretyAddress')),
      field('Referrer Name', val(data, 'refererName')),
      field('Referrer Mobile', val(data, 'refererNumber')),
    ].join(''),
  );

  const asset =
    kind === 'finance'
      ? section(
          assetTitle,
          [
            field('Registration No.', val(data, 'regNo')),
            field('HA Number', val(data, 'haNumber')),
            field('Maker / Brand', val(data, 'makers')),
            field('Model', val(data, 'model')),
            field('Chassis No.', val(data, 'chasisNo')),
            field('Engine No.', val(data, 'engNo')),
            field('Bank', val(data, 'bank')),
            field('Bank Transaction', val(data, 'bankTransaction')),
            field('Agreement No.', val(data, 'agreement', 'agreementFinance')),
          ].join(''),
        )
      : section(
          assetTitle,
          [
            field('Reference No.', val(data, 'haNumber', 'id')),
            field('Loan Type', val(data, 'loanType')),
            field('Purpose', val(data, 'purpose', 'recordDetails', 'loanDetails')),
          ].join(''),
        );

  const facility =
    kind === 'finance'
      ? section(
          'Facility & Repayment',
          [
            field('Principal Amount', `₹ ${val(data, 'totalAmount', 'amount')}`),
            field('Out Payment', `₹ ${val(data, 'outPayment')}`),
            field('Opening Interest', val(data, 'opInterest')),
            field('Interest (₹ per ₹100 / month)', val(data, 'interestPercentage', 'interestRate')),
            field('Total Interest', `₹ ${val(data, 'totalInterest')}`),
            field('Balance', `₹ ${val(data, 'amountBalance')}`),
            field('EMI Period (months)', val(data, 'emiPeriod')),
            field('EMI Amount', `₹ ${val(data, 'emiAmount')}`),
          ].join(''),
        )
      : section(
          'Facility & Repayment',
          (() => {
            const repaymentType = parseHandLoanRepaymentType(data.repaymentType, data.interestBasis);
            const termLabel = repaymentType === 'short_term' ? 'Term (days)' : 'Term (months)';
            return [
              field('Loan Amount', `₹ ${val(data, 'loanAmount', 'amount')}`),
              field('Repayment type', repaymentType === 'short_term' ? 'Short term (lump sum)' : 'EMI (monthly)'),
              field('Interest type', String(data.interestType || 'simple') === 'compound' ? 'Compound' : 'Simple'),
              field(HAND_LOAN_RATE_LABEL, val(data, 'interestRate')),
              field(termLabel, val(data, 'loanTerm', 'emiPeriod')),
            ].join('');
          })(),
        );

  const declaration = `
    <div style="margin-top:10px;border:1px solid #e2e8f0;border-radius:8px;padding:8px 12px;background:#f8fafc;break-inside:avoid;page-break-inside:avoid;">
      <div style="font-size:10.5px;color:#475569;line-height:1.55;">
        I/We hereby declare that the information furnished above is true and correct to the best of my/our knowledge and belief.
        I/We authorise the company to verify the above particulars and agree to repay the facility as per the agreed schedule.
      </div>
      <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:24px;margin-top:20px;">
        <div style="border-top:1px solid #94a3b8;padding-top:5px;font-size:10.5px;color:#475569;text-align:center;">Applicant Signature</div>
        <div style="border-top:1px solid #94a3b8;padding-top:5px;font-size:10.5px;color:#475569;text-align:center;">Guarantor Signature</div>
        <div style="border-top:1px solid #94a3b8;padding-top:5px;font-size:10.5px;color:#475569;text-align:center;">Authorised Signatory</div>
      </div>
    </div>`;

  const photo = mediaSrc(val(data, 'applicantPhoto') === '—' ? '' : val(data, 'applicantPhoto'));
  const photoBox =
    photo && photo !== '—'
      ? `<img src="${esc(photo)}" alt="Applicant" style="width:84px;height:104px;object-fit:cover;border:1px solid #cbd5e1;border-radius:6px;background:#fff;" />`
      : `<div style="width:84px;height:104px;border:1px dashed #cbd5e1;border-radius:6px;display:flex;align-items:center;justify-content:center;font-size:8px;color:#94a3b8;text-align:center;line-height:1.3;">Affix<br/>Photo</div>`;

  // 1024px PNG shown at ~64px — stays razor-sharp when the PDF is zoomed.
  const qrBox = opts?.qrDataUrl
    ? `<div style="text-align:center;">
        <img class="hd-qr" data-hd-qr="1" src="${opts.qrDataUrl}" width="1024" height="1024" alt="Document QR" style="width:64px;height:64px;${HD_QR_IMG_STYLE}border:1px solid #e2e8f0;border-radius:4px;background:#fff;display:block;" />
        <div style="font-size:7.5px;color:#94a3b8;margin-top:3px;letter-spacing:.04em;text-transform:uppercase;">Scan · Ref</div>
      </div>`
    : '';

  return `
    <div>
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;border:1px solid #cbd5e1;border-radius:8px;padding:8px 12px;background:#ffffff;margin-bottom:4px;break-inside:avoid;page-break-inside:avoid;">
        <div style="min-width:0;flex:1;">
          <div style="font-size:14px;font-weight:800;color:#0f172a;letter-spacing:.04em;">${esc(docTitle)}</div>
          <div style="font-size:11px;color:#64748b;margin-top:4px;">Ref: <strong style="color:#0f172a;">${esc(refNo)}</strong></div>
        </div>
        <div style="display:flex;align-items:flex-start;gap:8px;flex-shrink:0;">
          ${qrBox}
          ${photoBox}
        </div>
      </div>
      ${applicant}
      ${guarantor}
      ${asset}
      ${facility}
      ${declaration}
    </div>`;
}

export async function exportLoanApplicationPdf(data: Dict, kind: LoanKind): Promise<void> {
  const name = val(data, 'customerName');
  const title = kind === 'finance' ? `Finance Application — ${name}` : `Hand Loan Application — ${name}`;
  const refNo = val(data, 'haNumber', 'regNo', 'id');
  const mobile = val(data, 'mobileNumber', 'phone');
  let qrDataUrl: string | undefined;
  try {
    // Compact payload for scanners; 1024×1024 PNG with ECC H survives print + zoom.
    qrDataUrl = await generateHdQrDataUrl(
      `EdgeFinances|${kind}|${refNo}|${name}|${mobile === '—' ? '' : mobile}`,
      { size: 1024, margin: 2 }
    );
  } catch {
    qrDataUrl = undefined;
  }
  await exportHtmlToPdf(title, buildLoanApplicationHtml(data, kind, { qrDataUrl }), {
    compact: true,
    singlePage: true,
  });
}
